import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../lib/api';
import { resolveImageUrl } from '../../lib/utils';

interface Service {
  id: string;
  name: string;
  name_hindi?: string;
  description?: string;
  duration: number;
  price: number;
  category: string;
  image_url?: string;
}

const ServicesPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageSettings, setPageSettings] = useState({
    hero_title: 'Our Premium Services',
    hero_subtitle: 'Discover our comprehensive range of luxury grooming services',
    hero_image_url: ''
  });

  useEffect(() => {
    loadServicesFromAPI();
  }, []);

  const loadServicesFromAPI = async () => {
    try {
      setLoading(true);
      setError(null);

      const [servicesData, categoriesData, settings] = await Promise.all([
        apiClient.getServices(),
        apiClient.getServiceCategories(),
        apiClient.getServicesPageSettings()
      ]);

      setServices(servicesData);
      setCategories(['All Services', ...categoriesData.map((cat: any) => cat.name)]);
      setPageSettings(settings);
    } catch (err: any) {
      setError('Failed to load services. Please check your connection and try again.');
      console.error('Error loading services:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = services.filter(service =>
    activeCategory === 'all' || activeCategory === 'All Services' ||
    service.category.toLowerCase() === activeCategory.toLowerCase()
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 pt-20">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading services...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-primary-50 to-primary-100">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            {pageSettings.hero_title}
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
            {pageSettings.hero_subtitle}
          </p>
          {error && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded-lg max-w-md mx-auto mb-6">
              <div className="flex items-center">
                <i className="ri-cloud-off-line mr-2"></i>
                <span>Services loaded from cache. Some services may not be up to date.</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${activeCategory === 'all' || activeCategory === 'All Services'
                  ? 'bg-primary-400 text-black shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-primary-100 shadow-md'
                }`}
            >
              All Services
            </button>
            {categories.filter(cat => cat !== 'All Services').map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${activeCategory === category
                    ? 'bg-primary-400 text-black shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-primary-100 shadow-md'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Services Grid */}
          {filteredServices.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-service-line text-6xl text-gray-400 mb-4"></i>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Services Found</h3>
              <p className="text-gray-600 mb-8">
                {activeCategory === 'all' || activeCategory === 'All Services'
                  ? 'No services are currently available.'
                  : `No services found in the "${activeCategory}" category.`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredServices.map((service) => (
                <div
                  key={service.id}
                  className="bg-white rounded-2xl shadow-luxury p-6 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 group"
                >
                  {service.image_url && (
                    <div className="w-full h-48 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl mb-6 overflow-hidden">
                      <img
                        src={resolveImageUrl(service.image_url)}
                        alt={service.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          // Hide image if it fails to load
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {service.name}
                    </h3>
                    {service.name_hindi && (
                      <p className="text-lg text-primary-600 font-medium mb-2">
                        {service.name_hindi}
                      </p>
                    )}

                    {service.description && (
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {service.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between mb-6">
                      <div className="text-center flex-1">
                        <span className="text-3xl font-bold text-primary-400">₹{service.price}</span>
                      </div>
                      <div className="text-center flex-1">
                        <span className="text-sm text-gray-500 block">Duration</span>
                        <span className="text-lg font-semibold text-gray-700">{service.duration} min</span>
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 mb-4">
                      Category: {service.category}
                    </div>

                    <Link
                      to="/booking"
                      state={{ selectedService: service }}
                      className="block w-full bg-primary-400 hover:bg-primary-500 text-black py-3 rounded-xl font-bold transition-all duration-300 text-center"
                    >
                      Book Now
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready for Your <span className="text-primary-400">Transformation</span>?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Book your appointment today and experience the difference our professional services can make
          </p>
          <Link
            to="/booking"
            className="inline-block bg-primary-400 hover:bg-primary-500 text-black px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300"
          >
            Book Your Appointment
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;